import { useEffect, useCallback, useRef, useState } from "react";
import Intercom from "@intercom/messenger-js-sdk";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/context/ThemeContext";
import { useLocation } from "react-router-dom";
import { UserType } from "@/types/user";
import { clearIntercomData } from "@/utils/intercomUtils";
import { useIntercomLauncherVisibility } from "@/context/IntercomLauncherVisibilityContext";
import { useNativePlatform } from "@/hooks/useNativePlatform";
const INTERCOM_APP_ID = "dcw7ecc4";

const getIntercomWindow = () => {
  if (typeof window === "undefined") return null;
  return window as unknown as Window & {
    Intercom?: (action: string, data?: unknown) => void;
  };
};

export const IntercomWidget = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const { suppressIntercomLauncher } = useIntercomLauncherVisibility();
  const { isNative } = useNativePlatform();
  const isBootedRef = useRef(false);
  const previousUserRef = useRef<typeof user>(null);
  /** Synkad med isBootedRef så vi kan köra effekter (t.ex. hide launcher) efter boot */
  const [intercomBooted, setIntercomBooted] = useState(false);

  // Uppdatera Intercom tema
  const updateIntercomTheme = useCallback((currentTheme: string) => {
    const IntercomWindow = getIntercomWindow();
    if (!IntercomWindow?.Intercom) return;

    try {
      IntercomWindow.Intercom("update", {
        theme_mode: currentTheme === "dark" ? "dark" : "light",
      });
    } catch (error) {
      // Silent fail
    }
  }, []);

  // Shutdown Intercom
  const shutdownIntercom = useCallback(() => {
    const IntercomWindow = getIntercomWindow();
    if (!IntercomWindow?.Intercom || !isBootedRef.current) return;

    try {
      IntercomWindow.Intercom("shutdown");
      isBootedRef.current = false;
      setIntercomBooted(false);
    } catch (error) {
      // Silent fail
    }
  }, []);

  // Boot Intercom
  const bootIntercom = useCallback(() => {
    const IntercomWindow = getIntercomWindow();
    if (!IntercomWindow) return;

    // Shutdown om det redan finns en instans
    if (IntercomWindow.Intercom && isBootedRef.current) {
      shutdownIntercom();
      setTimeout(() => performBoot(), 200);
      return;
    }

    performBoot();

    function performBoot() {
      const native = isNative;
      const bootSettings = {
        app_id: INTERCOM_APP_ID,
        hide_default_launcher: native ? true : suppressIntercomLauncher,
      };

      try {
        if (user) {
          Intercom({
            ...bootSettings,
            user_id: user.id || user.email,
            email: user.email,
          });

          // Uppdatera med email efter boot
          setTimeout(() => {
            const IntercomWindow = getIntercomWindow();
            if (IntercomWindow?.Intercom) {
              try {
                IntercomWindow.Intercom("update", { email: user.email });
              } catch (error) {
                // Silent fail
              }
            }
          }, 1000);
        } else {
          Intercom(bootSettings);
        }

        isBootedRef.current = true;
        setIntercomBooted(true);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bilenia-intercom-ready"));
        }

        // Uppdatera tema efter boot
        setTimeout(() => updateIntercomTheme(theme), 500);
      } catch (error) {
        isBootedRef.current = false;
        setIntercomBooted(false);
      }
    }
  }, [user, theme, updateIntercomTheme, shutdownIntercom, isNative]);

  // Huvudlogik: Boot/shutdown baserat på user state
  useEffect(() => {
    const IntercomWindow = getIntercomWindow();
    if (!IntercomWindow) return;

    // Dölj för admins
    if (user?.type === UserType.Admin) {
      if (isBootedRef.current) {
        shutdownIntercom();
        clearIntercomData();
      }
      return;
    }

    // Rensa data vid första laddning
    if (previousUserRef.current === null && !isBootedRef.current) {
      clearIntercomData();
    }

    // Kolla om användaren har ändrats
    const userChanged =
      previousUserRef.current?.email !== user?.email ||
      (previousUserRef.current === null && user !== null) ||
      (previousUserRef.current !== null && user === null);

    // Logout
    if (userChanged && user === null) {
      if (isBootedRef.current) {
        shutdownIntercom();
        clearIntercomData();
      }
      previousUserRef.current = user;

      // Native: stay shut down after logout (avoids Fin opening on /auth)
      if (isNative) {
        return;
      }

      // Web: re-boot visitor mode so guests keep Fin chat
      setTimeout(() => {
        bootIntercom();
      }, 800);
      return;
    }

    // User changed (login / switch) — restart with identified user
    if (userChanged && isBootedRef.current) {
      shutdownIntercom();
      clearIntercomData();
      setTimeout(() => {
        bootIntercom();
        previousUserRef.current = user;
      }, 500);
      return;
    }

    // First boot — web includes anonymous guests; native only when logged in
    if (!isBootedRef.current) {
      if (!isNative || user) {
        bootIntercom();
        previousUserRef.current = user;
      }
    }
  }, [user, bootIntercom, shutdownIntercom, isNative]);

  // Uppdatera när URL ändras (SPA)
  useEffect(() => {
    const IntercomWindow = getIntercomWindow();
    if (!IntercomWindow?.Intercom || !isBootedRef.current) return;

    try {
      const updateData: { [key: string]: unknown } = {
        last_request_at: Math.floor(Date.now() / 1000),
      };

      if (user) {
        updateData.user_id = user.id || user.email;
        updateData.email = user.email;
      }

      IntercomWindow.Intercom("update", updateData);
    } catch (error) {
      // Silent fail
    }
  }, [location.pathname, user]);

  // Web: hide when bid panel overlaps. Native: show default launcher (bottom-right).
  useEffect(() => {
    const IntercomWindow = getIntercomWindow();
    if (!IntercomWindow?.Intercom || !intercomBooted) return;

    try {
      IntercomWindow.Intercom("update", {
        hide_default_launcher: isNative
          ? true
          : suppressIntercomLauncher,
      });
    } catch {
      // Silent fail
    }
  }, [suppressIntercomLauncher, intercomBooted, isNative]);

  // Uppdatera tema när theme ändras
  useEffect(() => {
    if (!isBootedRef.current) return;
    const timer = setTimeout(() => updateIntercomTheme(theme), 500);
    return () => clearTimeout(timer);
  }, [theme, updateIntercomTheme]);

  // Cleanup vid unmount
  useEffect(() => {
    return () => {
      if (isBootedRef.current) {
        shutdownIntercom();
        previousUserRef.current = null;
      }
    };
  }, [shutdownIntercom]);

  if (user?.type === UserType.Admin) {
    return null;
  }

  return null;
};
